import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiCalendar,
  FiMapPin,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { API_ENDPOINTS } from '../../utils/api';
import { TIME_SESSIONS, formatSessionDisplay, getPlanStatusBadgeClass } from '../../utils/dailyDoctorPlanUtils';

const VISIT_COUNT_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);

const parseISODate = (value) => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const formatDateISO = (d) => {
  const dt = parseISODate(d) || (d instanceof Date ? d : new Date());
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatDisplayDate = (value) => {
  if (!value) return '—';
  const dt = parseISODate(value);
  if (!dt) return value;
  const d = String(dt.getDate()).padStart(2, '0');
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const y = dt.getFullYear();
  return `${d}-${m}-${y}`;
};

const formatNavDate = (value) => {
  const dt = parseISODate(value);
  if (!dt) return '—';
  return dt.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatSno = (sno) => String(Number(sno) || 0).padStart(2, '0');

const shiftDate = (iso, days) => {
  const dt = parseISODate(iso);
  if (!dt) return iso;
  dt.setDate(dt.getDate() + days);
  return formatDateISO(dt);
};

const PlanEntryActions = ({ entry, onEdit, onDelete }) => (
  <div className="inline-flex items-center gap-3">
    <button
      type="button"
      onClick={() => onEdit(entry)}
      className="inline-flex items-center gap-1 text-teal-600 hover:text-teal-800 text-sm font-medium"
      title="Edit"
    >
      <FiEdit2 />
      <span className="md:hidden">Edit</span>
    </button>
    <button
      type="button"
      onClick={() => onDelete(entry._id)}
      className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 text-sm font-medium"
      title="Delete"
    >
      <FiTrash2 />
      <span className="md:hidden">Delete</span>
    </button>
  </div>
);

const DailyDoctorPlan = () => {
  const [selectedDate, setSelectedDate] = useState(formatDateISO(new Date()));
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    doctorName: '',
    location: '',
    noOfVisits: 1,
    timeSession: 'Morning',
    feedback: '',
  });

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const fetchPlans = useCallback(async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      const { data } = await axios.get(API_ENDPOINTS.getMyDailyDoctorPlans(selectedDate), {
        headers: authHeaders(),
      });
      setPlans(data.entries || []);
    } catch (err) {
      console.error('fetchPlans error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load daily plans',
        customClass: { container: 'swal-above-app-modal' },
      });
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const nextSno = useMemo(() => {
    if (!plans.length) return 1;
    const max = Math.max(...plans.map((p) => Number(p.sno) || 0));
    return max + 1;
  }, [plans]);

  const summaryRows = useMemo(() => {
    const totalVisits = plans.reduce((sum, p) => sum + (Number(p.noOfVisits) || 0), 0);
    const pending = plans.filter((p) => (p.status || 'Pending') === 'Pending').length;
    const approved = plans.filter((p) => p.status === 'Approved').length;
    const rejected = plans.filter((p) => p.status === 'Rejected').length;

    const sessionCounts = TIME_SESSIONS.map((s) => ({
      label: s.value,
      count: plans.filter((p) => p.timeSession === s.value).length,
    }));

    return {
      totalEntries: plans.length,
      totalVisits,
      pending,
      approved,
      rejected,
      sessionCounts,
    };
  }, [plans]);

  const goPrevDay = () => setSelectedDate((d) => shiftDate(d, -1));
  const goNextDay = () => setSelectedDate((d) => shiftDate(d, 1));
  const goToday = () => setSelectedDate(formatDateISO(new Date()));

  const openAddModal = () => {
    setEditingEntry(null);
    setForm({
      doctorName: '',
      location: '',
      noOfVisits: 1,
      timeSession: 'Morning',
      feedback: '',
    });
    setIsModalOpen(true);
  };

  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setForm({
      doctorName: entry.doctorName || '',
      location: entry.location || '',
      noOfVisits: entry.noOfVisits || 1,
      timeSession: entry.timeSession || 'Morning',
      feedback: entry.feedback || '',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingEntry(null);
    setSaving(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.doctorName.trim()) {
      Swal.fire('Error', 'Doctor name is required', 'error');
      return;
    }
    if (!form.noOfVisits || Number(form.noOfVisits) < 0) {
      Swal.fire('Error', 'No of visits is invalid', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        doctorName: form.doctorName,
        location: form.location,
        noOfVisits: Number(form.noOfVisits),
        timeSession: form.timeSession,
        feedback: form.feedback,
      };

      if (editingEntry?._id) {
        await axios.put(
          API_ENDPOINTS.updateDailyDoctorPlanEntry(editingEntry._id),
          payload,
          { headers: authHeaders() }
        );
      } else {
        await axios.post(
          API_ENDPOINTS.createDailyDoctorPlanEntry,
          {
            date: selectedDate,
            sno: nextSno,
            ...payload,
          },
          { headers: authHeaders() }
        );
      }

      closeModal();
      await fetchPlans();
      Swal.fire('Success', editingEntry ? 'Daily plan updated' : 'Daily plan added', 'success');
    } catch (err) {
      console.error('handleSubmit error:', err);
      Swal.fire('Error', err?.response?.data?.error || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete entry?',
      text: 'This plan entry will be removed.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      customClass: { container: 'swal-above-app-modal' },
    });
    if (!result.isConfirmed) return;

    try {
      await axios.delete(API_ENDPOINTS.deleteDailyDoctorPlanEntry(id), { headers: authHeaders() });
      await fetchPlans();
      Swal.fire('Deleted', 'Entry deleted successfully', 'success');
    } catch (err) {
      console.error('handleDelete error:', err);
      Swal.fire('Error', 'Failed to delete', 'error');
    }
  };

  const isToday = selectedDate === formatDateISO(new Date());

  return (
    <div className="-mx-4 md:mx-0 px-3 sm:px-4 md:px-0 pb-6 bg-gradient-to-br from-slate-100 via-teal-50/30 to-slate-100 min-h-screen">
      <div className="max-w-6xl mx-auto space-y-4">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1a365d] flex items-center gap-2 sm:gap-3">
                  <FiCalendar className="text-teal-600 shrink-0" />
                  Daily Plan
                </h1>
                <p className="text-slate-600 mt-1 text-sm sm:text-base">
                  Doctor visit plan (Morning / Afternoon / Evening)
                </p>
              </div>
              <button
                type="button"
                onClick={openAddModal}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-[#1a365d] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#152c4d] transition-colors"
              >
                <FiPlus />
                Add Entry
              </button>
            </div>

            {/* Date navigation */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center justify-between gap-2 w-full sm:w-auto sm:min-w-[280px] rounded-xl border border-slate-200 bg-slate-50 p-1">
                <button
                  type="button"
                  onClick={goPrevDay}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-teal-50 hover:text-teal-700 transition-colors shrink-0"
                  aria-label="Previous day"
                >
                  <FiChevronLeft className="text-lg" />
                </button>

                <div className="flex-1 text-center px-1 min-w-0">
                  <p className="text-sm sm:text-base font-bold text-[#1a365d] truncate">
                    {formatNavDate(selectedDate)}
                  </p>
                  <p className="text-xs text-slate-500">{formatDisplayDate(selectedDate)}</p>
                </div>

                <button
                  type="button"
                  onClick={goNextDay}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-teal-50 hover:text-teal-700 transition-colors shrink-0"
                  aria-label="Next day"
                >
                  <FiChevronRight className="text-lg" />
                </button>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                {!isToday && (
                  <button
                    type="button"
                    onClick={goToday}
                    className="flex-1 sm:flex-none px-3 py-2 rounded-lg border border-teal-200 bg-teal-50 text-teal-800 text-sm font-semibold hover:bg-teal-100"
                  >
                    Today
                  </button>
                )}
                <label className="flex-1 sm:flex-none flex items-center gap-2 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2">
                  <FiCalendar className="shrink-0 text-teal-600" />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full min-w-0 border-0 p-0 bg-transparent text-sm focus:outline-none focus:ring-0"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Daily entries */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-bold text-slate-800">Daily Entries</h2>
            <span className="text-xs sm:text-sm text-slate-500 font-medium">
              {loading ? 'Loading...' : `Total entries: ${plans.length}`}
            </span>
          </div>

          {loading ? (
            <div className="px-4 py-12 text-center text-slate-500">Loading entries...</div>
          ) : plans.length === 0 ? (
            <div className="px-4 py-12 text-center text-slate-500 text-sm">
              No entries for this date. Tap <b>Add Entry</b> to create one.
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-slate-100">
                {plans.map((p) => (
                  <div key={p._id} className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs text-slate-500">
                          SNO {formatSno(p.sno)} · {formatDisplayDate(p.date || selectedDate)}
                        </p>
                        <p className="font-semibold text-slate-900 mt-0.5">{p.doctorName}</p>
                      </div>
                      <span
                        className={`shrink-0 px-2 py-1 rounded-full text-xs font-semibold ${getPlanStatusBadgeClass(p.status)}`}
                      >
                        {p.status || 'Pending'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="rounded-lg bg-slate-50 p-2">
                        <p className="text-xs text-slate-500">Location</p>
                        <p className="font-medium text-slate-800 flex items-center gap-1 mt-0.5">
                          <FiMapPin className="shrink-0" />
                          <span className="truncate">{p.location || '—'}</span>
                        </p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-2">
                        <p className="text-xs text-slate-500">Visits</p>
                        <p className="font-medium text-slate-800 mt-0.5">{p.noOfVisits}</p>
                      </div>
                    </div>

                    <div className="rounded-lg bg-slate-50 p-2 text-sm">
                      <p className="text-xs text-slate-500">Time session</p>
                      <p className="font-medium text-slate-800 flex items-start gap-1 mt-0.5">
                        <FiClock className="shrink-0 mt-0.5" />
                        <span>{formatSessionDisplay(p.timeSession, p.timeFrom, p.timeTo)}</span>
                      </p>
                    </div>

                    {p.feedback ? (
                      <div className="rounded-lg bg-slate-50 p-2 text-sm">
                        <p className="text-xs text-slate-500">Feedback</p>
                        <p className="text-slate-800 mt-0.5">{p.feedback}</p>
                      </div>
                    ) : null}

                    <PlanEntryActions entry={p} onEdit={openEditModal} onDelete={handleDelete} />
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b">
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">SNO</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Date</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Doctor name</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Location</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">No of visits</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Time session</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Feedback</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Status</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((p) => (
                      <tr key={p._id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-600">{formatSno(p.sno)}</td>
                        <td className="px-4 py-3 text-slate-600">{formatDisplayDate(p.date || selectedDate)}</td>
                        <td className="px-4 py-3 font-medium">{p.doctorName}</td>
                        <td className="px-4 py-3 text-slate-600">
                          <span className="inline-flex items-center gap-2">
                            <FiMapPin />
                            {p.location || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{p.noOfVisits}</td>
                        <td className="px-4 py-3 text-slate-600">
                          <span className="inline-flex items-center gap-2">
                            <FiClock />
                            {formatSessionDisplay(p.timeSession, p.timeFrom, p.timeTo)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{p.feedback || '—'}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${getPlanStatusBadgeClass(p.status)}`}
                          >
                            {p.status || 'Pending'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <PlanEntryActions entry={p} onEdit={openEditModal} onDelete={handleDelete} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Summary table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200">
            <h2 className="font-bold text-slate-800">Day Summary</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Overview for {formatDisplayDate(selectedDate)}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Summary</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700">Count</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="px-4 py-3 text-slate-700">Total entries</td>
                  <td className="px-4 py-3 text-right font-semibold text-[#1a365d]">{summaryRows.totalEntries}</td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-3 text-slate-700">Total visits</td>
                  <td className="px-4 py-3 text-right font-semibold text-[#1a365d]">{summaryRows.totalVisits}</td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-3 text-slate-700">Pending</td>
                  <td className="px-4 py-3 text-right">
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800">
                      {summaryRows.pending}
                    </span>
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-3 text-slate-700">Approved</td>
                  <td className="px-4 py-3 text-right">
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
                      {summaryRows.approved}
                    </span>
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-3 text-slate-700">Rejected</td>
                  <td className="px-4 py-3 text-right">
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700">
                      {summaryRows.rejected}
                    </span>
                  </td>
                </tr>
                {summaryRows.sessionCounts.map((row) => (
                  <tr key={row.label} className="border-b last:border-b-0">
                    <td className="px-4 py-3 text-slate-700">{row.label} session</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-800">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-[2000] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-slate-200 w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between shrink-0">
              <h3 className="font-bold text-slate-800">
                {editingEntry ? 'Edit Daily Entry' : 'Add Daily Entry'}
              </h3>
              <button type="button" onClick={closeModal} className="text-slate-500 hover:text-slate-800 px-2">
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">No of visit *</span>
                  <select
                    value={form.noOfVisits}
                    onChange={(e) => setForm((prev) => ({ ...prev, noOfVisits: Number(e.target.value) }))}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 bg-white"
                  >
                    {VISIT_COUNT_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Time *</span>
                  <select
                    value={form.timeSession}
                    onChange={(e) => setForm((prev) => ({ ...prev, timeSession: e.target.value }))}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 bg-white"
                    required
                  >
                    {TIME_SESSIONS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Doctor name *</span>
                  <input
                    type="text"
                    value={form.doctorName}
                    onChange={(e) => setForm((prev) => ({ ...prev, doctorName: e.target.value }))}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5"
                    required
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Location</span>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5"
                  />
                </label>
              </div>

              <label className="block">
                <span className="text-sm font-semibold text-slate-700">Feedback content</span>
                <textarea
                  value={form.feedback}
                  onChange={(e) => setForm((prev) => ({ ...prev, feedback: e.target.value }))}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 min-h-[100px]"
                  placeholder="Enter feedback..."
                />
              </label>

              <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="w-full sm:w-auto px-4 py-2.5 border border-slate-300 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-[#1a365d] text-white font-semibold hover:bg-[#152c4d] disabled:opacity-60"
                >
                  {saving ? 'Saving...' : editingEntry ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyDoctorPlan;
